/**
 * Monitor compacto «en vivo» para el Resumen de la ficha tarjetas.
 *
 * No duplica la pestaña Monitorización: aquí solo hay una banda con CPU, RAM,
 * disco más lleno y servicios, alimentada por el mismo evento WebSocket
 * (`monitoring:data`) y por una carga inicial ligera de 6 h.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { useSocket } from '../contexts/SocketContext';

const MAX_PUNTOS = 24;

function Serie({ values, color }) {
  const vals = (values || []).map(Number).filter((v) => Number.isFinite(v));
  if (vals.length < 2) return <div className="sf2-live-nochart">—</div>;
  const points = vals
    .map((v, i) => {
      const y = 30 - (Math.max(0, Math.min(100, v)) / 100) * 26;
      return `${(i * 100) / (vals.length - 1)},${y}`;
    })
    .join(' ');
  const area = `0,30 ${points} 100,30`;
  return (
    <svg className="sf2-spark sf2-live-spark" viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden="true">
      <polygon points={area} fill={color} opacity="0.14" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function colorUso(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '#22c55e';
  if (n >= 90) return '#ef4444';
  if (n >= 75) return '#f59e0b';
  return '#22c55e';
}

function hace(fecha) {
  if (!fecha) return '';
  const segundos = Math.max(0, Math.round((Date.now() - fecha.getTime()) / 1000));
  if (segundos < 10) return 'ahora';
  if (segundos < 60) return `hace ${segundos} s`;
  const minutos = Math.round(segundos / 60);
  if (minutos < 60) return `hace ${minutos} min`;
  return `hace ${Math.round(minutos / 60)} h`;
}

export default function ServerLiveMonitor({
  serverId,
  companyId,
  hasAgent = false,
  onGo,
  /** `rack_element` (servidor), `workstation` (PC) o `virt_vm`. */
  ciType = 'rack_element',
}) {
  const socket = useSocket();
  const [puntos, setPuntos] = useState([]);
  const [ultimaVez, setUltimaVez] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [, refrescarReloj] = useState(0);

  const anadirPunto = useCallback((punto) => {
    setPuntos((prev) => [...prev, punto].slice(-MAX_PUNTOS));
    setUltimaVez(new Date());
  }, []);

  useEffect(() => {
    if (!serverId || !companyId) {
      setCargando(false);
      return undefined;
    }
    let cancelado = false;
    setCargando(true);
    const cargarMetricas = ciType === 'workstation'
      ? api.getWorkstationMetrics(serverId, companyId, 6)
      : api.getServerMetrics(serverId, companyId, 6);
    cargarMetricas
      .then((res) => {
        if (cancelado) return;
        const datos = Array.isArray(res?.data) ? res.data : [];
        setPuntos(datos.slice(-MAX_PUNTOS));
        const ultimo = datos[datos.length - 1];
        if (ultimo?.timestamp) {
          const fecha = new Date(ultimo.timestamp);
          if (!Number.isNaN(fecha.getTime())) setUltimaVez(fecha);
        }
      })
      .catch(() => { /* la ficha no debe romperse por métricas */ })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [serverId, companyId, ciType]);

  useEffect(() => {
    if (!socket || !serverId) return undefined;
    const onData = (payload) => {
      const id = payload?.server_id ?? payload?.workstation_id ?? payload?.id;
      if (Number(id) !== Number(serverId)) return;
      // El mismo id puede existir en un equipo de rack y en una estación: el
      // tipo es lo que distingue de quién son las métricas.
      const tipo = payload?.ci_type || 'rack_element';
      if (tipo !== ciType) return;
      anadirPunto({ ...payload, server_id: id });
    };
    socket.on('monitoring:data', onData);
    return () => socket.off('monitoring:data', onData);
  }, [socket, serverId, ciType, anadirPunto]);

  useEffect(() => {
    const timer = window.setInterval(() => refrescarReloj((n) => n + 1), 5000);
    return () => window.clearInterval(timer);
  }, []);

  const latest = puntos.length ? puntos[puntos.length - 1] : null;

  const disco = useMemo(() => {
    const volumenes = Array.isArray(latest?.logical_volumes) ? latest.logical_volumes : [];
    if (!volumenes.length) return null;
    return volumenes
      .slice()
      .sort((a, b) => Number(b.used_percent || 0) - Number(a.used_percent || 0))[0];
  }, [latest]);

  const servicios = Array.isArray(latest?.services) ? latest.services : [];
  const enMarcha = servicios.filter((s) => /running|activo/i.test(String(s.status || ''))).length;

  if (!hasAgent || !serverId) return null;

  const cpu = latest?.cpu_percent;
  const ram = latest?.ram_percent;
  const discoPct = disco?.used_percent;

  return (
    <div className="sf2-card sf2-live">
      <h3 className="sf2-live-head">
        <span className="sf2-live-title">
          <span className={`sf2-live-dot${latest ? '' : ' sf2-live-dot--idle'}`} />
          En vivo
        </span>
        <span className="sf2-live-when">
          {ultimaVez ? `actualizado ${hace(ultimaVez)}` : (cargando ? 'conectando…' : 'sin datos del agente')}
        </span>
        {onGo && (
          <button type="button" className="sf2-link" onClick={() => onGo('monitoring')}>
            Monitorización
          </button>
        )}
      </h3>

      <div className="sf2-live-grid">
        <div className="sf2-live-metric">
          <span>CPU</span>
          <b style={{ color: colorUso(cpu) }}>{cpu != null ? `${Math.round(cpu)}%` : '—'}</b>
          <Serie values={puntos.map((p) => p.cpu_percent)} color={colorUso(cpu)} />
        </div>
        <div className="sf2-live-metric">
          <span>RAM</span>
          <b style={{ color: colorUso(ram) }}>{ram != null ? `${Math.round(ram)}%` : '—'}</b>
          <Serie values={puntos.map((p) => p.ram_percent)} color={colorUso(ram)} />
        </div>
        <div className="sf2-live-metric">
          <span>Disco {disco?.name || disco?.mountpoint || ''}</span>
          <b style={{ color: colorUso(discoPct) }}>
            {discoPct != null ? `${Math.round(discoPct)}%` : '—'}
          </b>
          <small>
            {disco?.free_gb != null
              ? `${Number(disco.free_gb).toFixed(1)} GB libres`
              : 'Sin datos'}
          </small>
        </div>
        <div className="sf2-live-metric">
          <span>Servicios</span>
          <b style={{ color: servicios.length && enMarcha < servicios.length ? '#f59e0b' : '#22c55e' }}>
            {servicios.length ? `${enMarcha}/${servicios.length}` : '—'}
          </b>
          <small>{servicios.length ? 'en ejecución' : 'sin datos'}</small>
        </div>
      </div>
    </div>
  );
}
